import React, { FunctionComponent, useState } from "react";
import { ArrowLeftRight, Grid3x3, Mic, MicOff, Pause, Phone, Play, Settings, UserPlus } from "lucide-react";
import { Call } from "@twilio/voice-sdk";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ActiveCallSettings from "@/components/dialer/ActiveCallSettings";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

interface CallControlsProps {
  twilioCall?: Call | null;
  onKeypadPressed: () => void;
  onHangPressed: () => void;
  className?: string;
  hasMuteShortcut?: boolean;
  hasSettingsButton?: boolean;
  isOnHold?: boolean;
  onHoldPressed?: () => void;
  onTransferPressed?: () => void;
  onAddCallPressed?: () => void;
}

const CallControls: FunctionComponent<CallControlsProps> = ({
  twilioCall,
  onKeypadPressed,
  onHangPressed,
  className,
  hasMuteShortcut = false,
  hasSettingsButton = true,
  isOnHold = false,
  onHoldPressed,
  onTransferPressed,
  onAddCallPressed,
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isOnMute, setIsOnMute] = useState(false);
  useKeyboardShortcuts({ m: () => handleMuteToggle() }, hasMuteShortcut);

  const handleMuteToggle = () => {
    const nextMuted = !isOnMute;
    setIsOnMute(nextMuted);
    twilioCall?.mute(nextMuted);
    const audio = new Audio("/audio/dial/mute.m4a");
    audio.play().catch(() => {});
  };

  return (
    <>
      <div className={cn("flex items-center gap-3", className)}>
        <Button
          variant={isOnMute ? "default" : "outline"}
          size="icon"
          className={cn("h-10 w-10 rounded-full", isOnMute && "bg-primary hover:bg-primary/90")}
          onClick={handleMuteToggle}
          title={isOnMute ? "Unmute" : "Mute"}
        >
          {isOnMute ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={onKeypadPressed}
          title="Keypad"
        >
          <Grid3x3 className="h-4 w-4" />
        </Button>
        {onHoldPressed && (
          <Button
            variant={isOnHold ? "default" : "outline"}
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={onHoldPressed}
            title={isOnHold ? "Resume" : "Hold"}
          >
            {isOnHold ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </Button>
        )}
        {onTransferPressed && (
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={onTransferPressed}
            title="Transfer"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
        )}
        {onAddCallPressed && (
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={onAddCallPressed}
            title="Add call"
          >
            <UserPlus className="h-4 w-4" />
          </Button>
        )}
        {hasSettingsButton && (
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={() => setIsSettingsOpen(true)}
            title="Audio settings"
          >
            <Settings className="h-4 w-4" />
          </Button>
        )}
        <Button
          size="icon"
          className="h-11 w-11 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
          onClick={onHangPressed}
          title="Hang up"
        >
          <Phone className="h-5 w-5 rotate-135" />
        </Button>
      </div>

      <ActiveCallSettings isOpen={isSettingsOpen} setIsOpen={setIsSettingsOpen} />
    </>
  );
};

export default CallControls;
